import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";

// Handles the redirect from Emergent Google Auth.
// URL fragment contains: #session_id=<id>
// We POST it to /api/auth/google/session — the backend exchanges it with
// Emergent for the profile, upserts the user, and issues our JWT cookies.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash;
    const match = /session_id=([^&]+)/.exec(hash);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", null, {
          headers: { "X-Session-ID": sessionId },
        });
        setUser(data);
        // Clear the hash so it doesn't linger in the URL
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${data.name.split(" ")[0]}`);
        const dest = data.role === "admin" ? "/admin" : "/dashboard";
        navigate(dest, { replace: true });
      } catch (e) {
        toast.error("Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
  }, [location.hash, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <img src={BRAND.logoUrl} alt="" className="h-12 w-12 mx-auto object-contain" />
        <div className="mt-4 font-heading font-semibold text-navy" data-testid="auth-callback-msg">Signing you in…</div>
      </div>
    </div>
  );
}
